import { Typography } from "antd";
import React from "react";
import { Col, Row, Table } from "react-bootstrap";
import { numberFormater } from "../../utils";
import PropTypes from "prop-types";

const { Text } = Typography;

const BillingReportBody = ({ reportValues = {} }) => {
  const isUnbundled =
    reportValues?.energyCharge !== undefined ||
    reportValues?.capacityCharge !== undefined ||
    reportValues?.retailCharge !== undefined;

  return (
    <div className="p-4">
      <Row xs={1} md={2}>
        {/* Report Information */}
        <Col>
          <h5 className="mb-4 fw-bold">Report Information</h5>
          {reportFields.map((field, index) => (
            <Row key={index} xs={1} md={2} className="align-items-center">
              <Col className="my-2 flex justify-between">
                <Text>{field.label}</Text>
              </Col>
              <Col className="my-2 flex justify-between">
                <p className="bg-gray-300 px-2 py-1 w-100 rounded-2 mb-0" style={{ minHeight: 30 }}>
                  {reportValues[field?.value] || ""}
                </p>
              </Col>
            </Row>
          ))}
        </Col>

        {/* Electricity Usage Information */}
        <Col>
          <h5 className="mb-4 fw-bold">Electricity Usage Information</h5>

          {/* Table 1: Electricity Usage Breakdown */}
          <Table bordered responsive className="mt-1">
            <thead className="text-white text-center">
              <tr>
                <th style={{ background: "#184C78" }} className="text-white" colSpan={2}>
                  Electricity Usage Breakdown
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="px-3">Total Electricity Usage (kWh)</td>
                <td className="px-3 text-end">
                  {numberFormater(reportValues?.totalUsageKwh ?? reportValues?.totalElectricityValue ?? 0)}
                </td>
              </tr>
              <tr>
                <td className="px-3">Maximum Demand Value (kW)</td>
                <td className="px-3 text-end">
                  {numberFormater(reportValues?.maximumDemandKw ?? reportValues?.demandTotalValue ?? 0)}
                </td>
              </tr>
            </tbody>
          </Table>

          {/* Table 2: Electricity Bill Cost Breakdown */}
          <Table bordered responsive className="mt-4">
            <thead className="text-white text-center">
              <tr>
                <th style={{ background: "#184C78" }} className="text-white" colSpan={2}>
                  Electricity Bill Cost Breakdown
                </th>
              </tr>
            </thead>
            <tbody>
              {isUnbundled ? (
                <>
                  <tr>
                    <td className="px-3">Energy Charge (RM)</td>
                    <td className="px-3 text-end">
                      {numberFormater(reportValues?.energyCharge ?? reportValues?.totalElectricityPrice ?? 0)}
                    </td>
                  </tr>
                  <tr>
                    <td className="px-3">Capacity Charge (RM)</td>
                    <td className="px-3 text-end">{numberFormater(reportValues?.capacityCharge ?? 0)}</td>
                  </tr>
                  <tr>
                    <td className="px-3">Network Charge (RM)</td>
                    <td className="px-3 text-end">{numberFormater(reportValues?.networkCharge ?? 0)}</td>
                  </tr>
                  <tr>
                    <td className="px-3">Retail Charge (RM)</td>
                    <td className="px-3 text-end">{numberFormater(reportValues?.retailCharge ?? 0)}</td>
                  </tr>
                  <tr>
                    <td className="px-3">AFA Rate (RM)</td>
                    <td className="px-3 text-end">{numberFormater(reportValues?.afaCharge ?? 0)}</td>
                  </tr>
                  <tr>
                    <td className="px-3">Energy Efficiency Incentive (RM)</td>
                    <td className="px-3 text-end">{numberFormater(reportValues?.incentiveCharge ?? 0)}</td>
                  </tr>
                  <tr>
                    <td className="px-3">Service Tax ({reportValues?.type ?? "0"}%)</td>
                    <td className="px-3 text-end">{numberFormater(reportValues?.serviceTax ?? 0)}</td>
                  </tr>
                  <tr className="fw-bold">
                    <td className="px-3">Total Charge (RM)</td>
                    <td className="px-3 text-end">{numberFormater(reportValues?.totalCharge ?? 0)}</td>
                  </tr>
                </>
              ) : (
                <>
                  {(electricityData(reportValues?.reportType, reportValues?.type) || []).map((item, index) => (
                    <tr key={index} className={`${item?.bold ? "fw-bold" : ""}`}>
                      <td className="px-3">{item?.name}</td>
                      <td className="px-3 text-end">{numberFormater(reportValues[item?.value]) || "0.00"}</td>
                    </tr>
                  ))}
                </>
              )}
            </tbody>
          </Table>
        </Col>
      </Row>
    </div>
  );
};

export default BillingReportBody;

BillingReportBody.propTypes = {
  reportValues: PropTypes.object,
};

const reportFields = [
  { label: "Report Generation Date", value: "reportGenerationDate" },
  { label: "Monthly Cycle Selected", value: "monthlyCycleSelected" },
  { label: "Site Name", value: "site" },
  { label: "Site Address", value: "siteAddress1" },
  { label: "", value: "siteAddress2" },
  { label: "Meter ID", value: "device" },
  { label: "Installed Area", value: "installedArea" },
  { label: "Tariff Category", value: "tariffName" },
];

const electricityData = (reportType, tax) => {
  let data = [
    { name: "Maximum Demand (RM)", value: "demandTotalPrice" },
    { name: `Service Tax (${tax ?? "0"}%) `, value: "serviceTax" },
    { name: "Total Charge (RM)", value: "totalCharge", bold: true },
  ];
  if (reportType === "type1") {
    data = [
      { name: "Peak Usage This Month (RM)", value: "peakHourElectricityPrice" },
      { name: "Off Peak Usage This Month (RM)", value: "offPeakHourElectricityPrice" },
      ...data,
    ];
  } else if (reportType === "type2") {
    data = [{ name: "All Usages This Month (RM)", value: "totalElectricityPrice" }, ...data];
  }
  return data;
};
